// Keranjang Pesanan — tipe & helper bersama (dipakai admin board & buyer).
//
// Model: tiap pembeli punya satu "keranjang" berisi barangnya dalam 2
// kategori (bucket): "belum_ready" (PO masih proses) & "ready" (barang
// fisik sudah ada — ready stok atau PO yang sudah datang). Status
// pembayaran melekat per-PCS (order_items.paid_qty, redesign mig 071/072)
// dan hanya badge per item, bukan kategori.

import {
  renderWaTemplate,
  resolveWaTemplate,
  type WaTemplateOverrides,
} from "@/lib/waTemplates";
import { HANGUS_CHIP_LABEL, type InvoiceKind, type InvoiceStatus } from "@/lib/invoices";
import { formatPrice } from "@/lib/pricing";
import {
  HANGUS_CHAIN_MAX_HOPS,
  judgeHangusCause,
  type HangusCauseNode,
  type HangusCauseVerdict,
  type HangusOrphanReason
} from "@/lib/dpHangusCause";

export type CartBucket = "belum_ready" | "ready";

export type CartBadge =
  | "lunas"
  | "sebagian"
  | "belum_lunas"
  | "dp_lunas"
  | "dp_belum_lunas"
  | "dp_hangus"
  | "gagal_produksi"
  | "cancel";

/**
 * Turunkan badge bayar sebuah item keranjang (model per-pcs):
 *   - cancelled + alasan gagal_produksi      → gagal_produksi
 *   - cancelled lainnya                      → cancel
 *   - paidQty = qty (atau paid_keep lama)    → lunas
 *   - 0 < paidQty < qty                      → sebagian ("X/Y Lunas")
 *   - paidQty = 0 + produk ber-DP            → dp_lunas / dp_belum_lunas
 *     (dari status nota DP order tsb), atau dp_hangus bila nota DP-nya
 *     hangus karena tagihan barang sudah terbit (mig 6590) — yang harus
 *     dibayar tagihan barangnya, bukan DP. Nota DP KEDALUWARSA (barang sudah
 *     ready semua sebelum DP dibayar, mig 7130) → belum_lunas: DP tak lagi
 *     berlaku, yang dibayar pelunasan barangnya. Baris yang SELURUH pcs-nya
 *     sudah ditagih di tagihan barang hidup (`billedQty`, mig 8000) selagi
 *     nota DP pesanannya belum lunas → dp_hangus juga: sejak 8000 nota DP
 *     tetap terbuka untuk pcs sisa, tapi baris itu tak ber-DP lagi (bughunt
 *     G8-8). Tertagih sebagian tetap dp_belum_lunas — pcs sisanya masih
 *     ber-DP; nota DP lunas tetap dp_lunas (DP-nya jadi Kredit DP)
 *   - selain itu                             → belum_lunas
 */
export function deriveCartBadge(input: {
  keepStatus: string | null | undefined;
  cancelReason?: string | null;
  paidQty: number | null | undefined;
  qty: number;
  dpRequired: boolean | null | undefined;
  dpInvoiceStatus?: InvoiceStatus | null;
  /** Nota DP pesanan ini hangus (tak ada nota DP aktif pengganti). */
  dpHangus?: boolean;
  /** Nota DP pesanan ini kedaluwarsa — barang sudah ready (mig 7130). */
  dpExpired?: boolean;
  /**
   * Pcs baris ini yang sudah ditagih di tagihan barang hidup (`billedByItem`
   * dari `buildCartInvoiceIndex`, mig 8000). Absen = 0 (perilaku lama).
   * Sengaja TIDAK dioper `buildCartBuyersFromRows` (board.ts): RPC
   * `admin_cart_board` tak membawa pcs tertagih, jadi baris tertagih penuh di
   * sana tetap dp_belum_lunas. Aman selama badge board tak dirender per item
   * (OrderBoard tidak; AdminCustomerEdit memetakan semua dp_* ke label
   * cadangannya) — begitu dirender, RPC-nya harus membawa pcs tertagih dulu.
   */
  billedQty?: number | null;
}): CartBadge {
  if (input.keepStatus === "cancelled") {
    return input.cancelReason === "gagal_produksi" ? "gagal_produksi" : "cancel";
  }

  const paidQty = Math.max(0, input.paidQty ?? 0);
  if (input.qty > 0 && paidQty >= input.qty) return "lunas";
  if (input.keepStatus === "paid_keep") return "lunas";
  if (paidQty > 0) return "sebagian";

  if (input.dpRequired) {
    if (input.dpInvoiceStatus === "paid") return "dp_lunas";
    // mig 7130: DP tak berlaku lagi untuk barang yang sudah ready — jangan
    // menyuruh "DP Belum Lunas" atas nota yang tak bisa dibayar.
    if (input.dpExpired) return "belum_lunas";
    // mig 8000: nota DP yang masih terbuka hanya menagih pcs yang BELUM masuk
    // tagihan barang — baris yang sudah ditagih penuh dibayar lewat tagihan
    // barangnya, bukan disuruh "DP Belum Lunas" (bughunt G8-8).
    const billedQty =
      input.billedQty != null && Number.isFinite(input.billedQty)
        ? Math.max(0, input.billedQty)
        : 0;
    if (input.qty > 0 && billedQty >= input.qty) return "dp_hangus";
    return input.dpHangus ? "dp_hangus" : "dp_belum_lunas";
  }
  return "belum_lunas";
}

export const cartBadgeLabel: Record<CartBadge, string> = {
  lunas: "Lunas",
  sebagian: "Lunas Sebagian",
  belum_lunas: "Belum Lunas",
  dp_lunas: "DP Lunas",
  dp_belum_lunas: "DP Belum Lunas",
  dp_hangus: HANGUS_CHIP_LABEL,
  gagal_produksi: "Gagal Produksi",
  cancel: "Batal"
};

/**
 * Label badge dengan progres pcs: badge "sebagian" → "3/10 Lunas", dan badge
 * "dp_lunas" → "DP Lunas Rp 20.000" bila nilai DP lunas BARIS itu diketahui
 * (`allocateDpPaidByRow`, putusan pemilik 2026-10-29: nilai per baris, bukan
 * total tagihan DP). Nilai 0 / kosong → label polos "DP Lunas".
 */
export function formatCartBadgeLabel(
  badge: CartBadge,
  paidQty?: number | null,
  qty?: number | null,
  dpRowAmount?: number | null
): string {
  if (badge === "sebagian" && paidQty != null && qty != null && qty > 0) {
    return `${Math.max(0, paidQty)}/${qty} Lunas`;
  }
  if (badge === "dp_lunas" && dpRowAmount != null && dpRowAmount > 0) {
    return `${cartBadgeLabel.dp_lunas} ${formatPrice(dpRowAmount)}`;
  }
  return cartBadgeLabel[badge];
}

/** Kelas warna pill per badge (Tailwind). */
export const cartBadgeColor: Record<CartBadge, string> = {
  lunas: "bg-leaf/15 text-leaf",
  sebagian: "bg-sky-100 text-sky-700",
  belum_lunas: "bg-marigold/15 text-marigold",
  dp_lunas: "bg-amber-100 text-amber-700",
  dp_belum_lunas: "bg-orange-100 text-orange-600",
  dp_hangus: "bg-zinc-100 text-zinc-600",
  gagal_produksi: "bg-red-100 text-red-600",
  cancel: "bg-zinc-100 text-zinc-500"
};

// ─── Urutan keranjang: pesanan gagal produksi di DASAR ───────────────────────

/**
 * Pesanan yang SELURUH item keranjangnya gagal produksi — tak ada lagi barang
 * hidup di dalamnya, hanya badge merah + catatan refund.
 *
 * SENGAJA menuntut "semua", bukan "ada satu": pada gagal SEBAGIAN pembeli masih
 * menunggu varian lain berjalan di produksi, jadi kartunya harus tetap di
 * urutan normal. Mendorongnya ke dasar akan menyembunyikan pesanan yang justru
 * masih perlu ditindaklanjuti.
 *
 * Daftar kosong → false: "tak punya item" bukan "gagal".
 */
export function isFullyFailedCartOrder(
  items: { keepStatus?: string | null; cancelReason?: string | null }[]
): boolean {
  if (!items || items.length === 0) return false;
  return items.every(
    (i) => i.keepStatus === "cancelled" && i.cancelReason === "gagal_produksi"
  );
}

/**
 * Dorong pesanan yang cocok predikat ke DASAR daftar, urutan relatif di dalam
 * tiap grup dipertahankan.
 *
 * Bergantung pada Array#sort yang STABIL (dijamin spesifikasi sejak ES2019),
 * sehingga urutan `created_at desc` dari server tetap berlaku di dalam grup
 * "masih jalan" maupun grup "gagal". Comparator sengaja hanya membandingkan
 * flag-nya — menambahkan tie-break tanggal di sini akan menduplikasi urutan
 * server dan diam-diam berbeda saat servernya diubah.
 */
export function sortFailedOrdersLast<T>(
  orders: T[],
  isFailed: (order: T) => boolean
): T[] {
  return [...(orders ?? [])].sort(
    (a, b) => Number(isFailed(a)) - Number(isFailed(b))
  );
}

// ─── Ringkasan kolom keranjang pembeli (/orders) ─────────────────────────────
//
// Baris kecil di bawah tombol sub-kolom Belum Ready / Sudah Ready (permintaan
// pemilik 2026-09-30), menggantikan keterangan statis kedua kolom — "Barang
// belum ada — pesanan yang PO-nya masih dibuka bisa diedit" dan "Barang sudah
// ada — pcs lunas bisa dikirim". Keduanya tetap terbaca dari kartunya sendiri
// (tombol Edit Pesanan; centang + Request Kirim hanya untuk pcs lunas):
//
//   5 produk 30 pcs | 25 pcs lunas 5 pcs belum lunas | total belum lunas = Rp 1.250.000
//
// ANGKANYA ANGKA KARTU, BUKAN HITUNGAN BARU:
//   • Item batal (Gagal Produksi) tak dihitung — `OrderCard` sudah membuangnya
//     dari total kartu, dan Rekap Barang WA admin (`buildCartRecapText`) juga.
//   • Lunas dipecah PER-PCS dari `paidQty`, model badge "3/10 Lunas"
//     (`deriveCartBadge`, mig 071/072): baris qty 3 ber-paid 1 menyumbang
//     1 pcs lunas + 2 pcs belum lunas. Cabang `paid_keep` tanpa `paid_qty`
//     yang dibela `deriveCartBadge` tak punya baris lagi di PROD (dicek
//     2026-09-30: nol `paid_keep` ber-`paid_qty` < qty), jadi `paidQty` saja
//     sudah sejalan dengan badge "Lunas".
//   • Rupiah = harga baris × pcs belum lunas, harganya `CartPriceView` yang
//     SAMA dengan baris harga kartu. Baris "harga menyusul" tetap dihitung
//     pcs-nya tapi tidak rupiahnya → `unpricedPcs`, ditulis terang "+ N pcs
//     harga menyusul" (pemilik 2026-09-30: "+" polos tak terbaca pembeli, dan
//     "Rp 0 +" — semua pcs belum berharga, mis. OPEN PO — terbaca "gratis").
//   • DP LUNAS DIPOTONG dari total (putusan pemilik 2026-09-30, bughunt
//     E7-1) — uangnya sudah diterima dan tagihan barang memotongnya sebagai
//     `dp_credit`, jadi "total belum lunas" tanpa potongan menagihnya dua
//     kali. Yang dipotong hanya DP yang BELUM tercermin sebagai pcs lunas
//     (`cartDpCreditByOrder`): Kredit DP yang sudah terpasang di tagihan
//     barang open/partial/paid ikut menutup pcs (`compute_invoice_allocation`)
//     sehingga sudah tak terhitung belum lunas. Potongan dibatasi sisa rupiah
//     pesanan itu di kolomnya, dan dibagi ke Sudah Ready dulu
//     (`splitCartDpCredit`) supaya pesanan yang terbelah dua kolom tak
//     terpotong dua kali. Sisa kredit yang tak genap satu pcs tetap tak
//     terpotong — batas model per-pcs, sama dengan pembayaran sebagian.
//   • Produk = kunci produk BERBEDA yang masih punya pcs hidup. Belum Ready
//     memakai id produk (dua pesanan produk sama = 1 produk); Sudah Ready
//     memakai kunci kartunya (`groupItemsByProduct`: satu kartu per produk).
//     Angka di tombol sub-kolom menghitung hal lain — kartu pesanan / baris
//     varian — dan sengaja tak disamakan.

/** Satu item kolom keranjang yang ikut diringkas. */
export type CartColumnSummaryItem = {
  /** Pesanan pemilik item — kunci potongan DP. Kosong = tanpa potongan. */
  orderId?: string;
  qty: number;
  paidQty: number | null | undefined;
  keepStatus: string | null | undefined;
  /**
   * Harga satuan baris — `CartPriceView.unitPrice` yang dipajang kartunya.
   * `null` = harga menyusul (kartunya pun tak merender baris harga).
   */
  unitPrice: number | null;
  /**
   * Uang bayar nota barang yang sudah menempel pcs belum lunas item ini tapi
   * belum genap satu pcs (`cartPaidResidual`, pemilik 2026-10-24). Absen = 0.
   */
  paidResidual?: number | null;
};

/** Satu kartu kolom keranjang: pesanan (Belum Ready) atau produk (Sudah Ready). */
export type CartColumnSummaryCard = {
  /** Kunci produk — kartu berkunci sama dihitung satu produk. */
  productKey: string;
  /** Item yang dirender kartunya. Kosong = pesanan tamu (localStorage). */
  items: readonly CartColumnSummaryItem[];
  /**
   * Total kartu pesanan TAMU tanpa item remote (hanya Belum Ready) — cermin
   * fallback `OrderCard` (`order.totalQty`/`order.totalPrice`). Tamu tak
   * punya nota di app, jadi seluruh pcs-nya belum lunas. Diabaikan bila
   * `items` berisi.
   */
  totalQty?: number;
  totalPrice?: number | null;
};

export type CartColumnSummary = {
  /** Produk berbeda yang masih punya pcs hidup. */
  products: number;
  pcs: number;
  paidPcs: number;
  unpaidPcs: number;
  /**
   * Rupiah pcs belum lunas yang harganya sudah ada, SESUDAH dipotong
   * pembayaran sebagian (`partialPaidDeducted`) dan DP terbayar (`dpDeducted`).
   */
  unpaidTotal: number;
  /**
   * Pcs belum lunas yang harganya belum ada (harga menyusul) — tak ikut
   * `unpaidTotal`, jadi totalnya belum lengkap selama ini > 0.
   */
  unpricedPcs: number;
  /** DP terbayar yang sudah dipotong dari `unpaidTotal` (0 = tak ada). */
  dpDeducted: number;
  /**
   * Uang bayar nota barang yang belum genap satu pcs, sudah dipotong dari
   * `unpaidTotal` (0 = tak ada) — `CartColumnSummaryItem.paidResidual`.
   */
  partialPaidDeducted: number;
};

/** Bilangan pcs yang aman dijumlah: bulat, tak negatif, NaN → 0. */
function wholePcs(n: number | null | undefined): number {
  return Math.max(0, Math.floor(Number(n) || 0));
}

/** Bayar sebagian yang boleh dipotong dari rupiah belum lunas sebesar `unpaidRp`. */
function residualTake(item: CartColumnSummaryItem, unpaidRp: number): number {
  return Math.min(Math.max(0, unpaidRp), Math.max(0, Number(item.paidResidual) || 0));
}

/**
 * Rupiah pcs belum lunas SATU item yang masih tersisa untuk DP (harga
 * menyusul/batal → 0) — SESUDAH bayar sebagiannya, supaya DP tak dibagikan ke
 * kolom yang rupiahnya sudah tertutup uang.
 */
function itemUnpaidRupiah(item: CartColumnSummaryItem): number {
  if (item.keepStatus === "cancelled" || item.unitPrice == null) return 0;
  const qty = wholePcs(item.qty);
  const unpaid = qty - Math.min(qty, wholePcs(item.paidQty));
  const rp = item.unitPrice * unpaid;
  return rp - residualTake(item, rp);
}

/**
 * @param dpCreditByOrder DP terbayar per pesanan yang boleh dipotong di kolom
 *   ini (`splitCartDpCredit`). Potongan per pesanan dibatasi rupiah belum
 *   lunas pesanan itu di kartu-kartu yang diringkas — DP tak pernah membuat
 *   total negatif atau memotong pesanan lain.
 *
 * Bayar sebagian (`paidResidual`) dipotong per item LEBIH DULU, lalu DP
 * dibatasi sisa pesanannya — jumlah keduanya tak pernah melebihi harga pcs.
 */
export function summarizeCartColumn(
  cards: readonly CartColumnSummaryCard[],
  dpCreditByOrder?: ReadonlyMap<string, number>
): CartColumnSummary {
  const products = new Set<string>();
  let pcs = 0;
  let paidPcs = 0;
  let unpaidTotal = 0;
  let unpricedPcs = 0;
  let partialPaidDeducted = 0;
  const unpaidByOrder = new Map<string, number>();

  for (const card of cards ?? []) {
    let cardPcs = 0;
    if (card.items.length) {
      for (const item of card.items) {
        if (item.keepStatus === "cancelled") continue;
        const qty = wholePcs(item.qty);
        const paid = Math.min(qty, wholePcs(item.paidQty));
        const unpaid = qty - paid;
        cardPcs += qty;
        paidPcs += paid;
        if (unpaid === 0) continue;
        if (item.unitPrice == null) unpricedPcs += unpaid;
        else {
          const rp = item.unitPrice * unpaid;
          const take = residualTake(item, rp);
          unpaidTotal += rp;
          partialPaidDeducted += take;
          if (item.orderId) {
            unpaidByOrder.set(
              item.orderId,
              (unpaidByOrder.get(item.orderId) ?? 0) + rp - take
            );
          }
        }
      }
    } else {
      // Pesanan tamu: kartunya menganggap Rp 0 = "Harga belum tersedia".
      cardPcs = wholePcs(card.totalQty);
      const total = Number(card.totalPrice) || 0;
      if (cardPcs > 0) {
        if (total > 0) unpaidTotal += total;
        else unpricedPcs += cardPcs;
      }
    }
    if (cardPcs > 0) products.add(card.productKey);
    pcs += cardPcs;
  }

  let dpDeducted = 0;
  if (dpCreditByOrder) {
    for (const [orderId, unpaid] of unpaidByOrder) {
      const credit = Math.max(0, Number(dpCreditByOrder.get(orderId)) || 0);
      dpDeducted += Math.min(credit, Math.max(0, unpaid));
    }
  }

  return {
    products: products.size,
    pcs,
    paidPcs,
    unpaidPcs: pcs - paidPcs,
    unpaidTotal: unpaidTotal - partialPaidDeducted - dpDeducted,
    unpricedPcs,
    dpDeducted,
    partialPaidDeducted
  };
}

// ─── Potongan DP di ringkasan keranjang (bughunt E7-1) ───────────────────────

/**
 * Status tagihan barang yang Kredit DP-nya SUDAH tercermin sebagai pcs lunas:
 * `apply_invoice_allocation` berjalan untuk open/partial/paid dan menutup pcs
 * dari `credit_absorbed` (`compute_invoice_allocation`). `pending_acc` sengaja
 * TIDAK ada — alokasinya belum jalan, jadi kreditnya belum menutup pcs dan
 * masih harus dipotong di keranjang. Beda satu status inilah satu-satunya
 * selisih dari rumus server `order_dp_credit_available`.
 */
export const DP_CREDIT_REFLECTED_STATUSES = ["open", "partial", "paid"] as const;

/** Baris nota DP mentah beserta buku penerimaannya. */
export type DpPaidRowLike = {
  status: string;
  source_order_id: string | null;
  amount_paid?: number | string | null;
  balance_applied?: number | string | null;
  refunded_amount?: number | string | null;
};

/** Baris `dp_credit` tagihan barang mentah (+ status notanya bila di-embed). */
export type DpCreditLineLike = {
  order_id: string | null;
  amount: number | string | null;
  invoices?: { status?: string | null } | Array<{ status?: string | null }> | null;
};

function num(v: unknown): number {
  const n = typeof v === "string" ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Uang BERSIH yang diterima sebuah nota DP (saldo + transfer − refund, ≥ 0) —
 * nominal "DP Lunas Rp …" di keranjang admin & pembeli, dan suku pertama
 * `cartDpCreditByOrder`. Sengaja BUKAN nominal Wajib DP / jumlah baris nota:
 * DP yang dikunci sebesar yang sudah dibayar (tagihan barang terbit sebelum DP
 * lunas) atau sudah direfund sebagian harus tampil sebesar uangnya — angka itu
 * pula yang dipotong jadi Kredit DP (`order_dp_credit_available`). Bukan pula
 * SISA kredit: DP yang sudah terpotong di tagihan barang tetap tampil penuh.
 */
export function dpInvoiceSettledAmount(
  row: Pick<DpPaidRowLike, "amount_paid" | "balance_applied" | "refunded_amount">
): number {
  return Math.max(
    0,
    num(row.balance_applied) + num(row.amount_paid) - num(row.refunded_amount)
  );
}

/**
 * DP terbayar per pesanan yang BELUM tercermin sebagai pcs lunas — bahan
 * potongan "total belum lunas" keranjang:
 *
 *   Σ uang bersih nota DP `paid` (saldo + transfer − refund)
 *   − Σ |Kredit DP| di tagihan barang open/partial/paid
 *
 * Cermin `order_dp_credit_available` (hanya nota DP LUNAS yang jadi kredit,
 * uang BERSIH), minus `pending_acc` (lihat `DP_CREDIT_REFLECTED_STATUSES`).
 * Baris kredit ber-status di luar daftar itu diabaikan, jadi pemanggil boleh
 * menyaring di query atau tidak. Pesanan bernilai 0 tak dimasukkan.
 *
 * @param unconvertedByOrder Kredit DP di nota open/partial yang BELUM jadi pcs
 *   lunas (`cartPaidResidual`, audit pemilik 2026-10-24). Kredit itu dikurangi
 *   penuh di atas, padahal pcs baru tertutup bila uangnya genap satu pcs —
 *   tanpa pengembalian ini DP Rp 200.000 atas pcs Rp 435.000 lenyap dari
 *   keranjang (nota #5608). Absen = perilaku lama.
 */
export function cartDpCreditByOrder(
  dpRows: ReadonlyArray<DpPaidRowLike>,
  creditLines: ReadonlyArray<DpCreditLineLike>,
  unconvertedByOrder?: ReadonlyMap<string, number> | null
): Map<string, number> {
  const reflected = new Set<string>(DP_CREDIT_REFLECTED_STATUSES);
  const net = new Map<string, number>();
  for (const r of dpRows ?? []) {
    if (r.status !== "paid" || !r.source_order_id) continue;
    const paid = dpInvoiceSettledAmount(r);
    net.set(r.source_order_id, (net.get(r.source_order_id) ?? 0) + paid);
  }
  for (const l of creditLines ?? []) {
    if (!l.order_id || !net.has(l.order_id)) continue;
    const inv = Array.isArray(l.invoices) ? l.invoices[0] : l.invoices;
    if (inv && !reflected.has(inv.status ?? "")) continue;
    net.set(l.order_id, (net.get(l.order_id) ?? 0) - Math.abs(num(l.amount)));
  }
  for (const [orderId, v] of unconvertedByOrder ?? []) {
    if (!net.has(orderId)) continue;
    net.set(orderId, (net.get(orderId) ?? 0) + Math.max(0, num(v)));
  }
  const out = new Map<string, number>();
  for (const [orderId, v] of net) if (v > 0) out.set(orderId, v);
  return out;
}

/**
 * Peta DP per pesanan dari item keranjang — nilainya diduplikasi ke tiap item
 * pesanan (pola `dpAmount`), jadi item pertama yang membawanya yang dipakai.
 */
export function dpCreditMapFromItems(
  items: ReadonlyArray<{ orderId: string; dpCreditLeft?: number | null }>
): Map<string, number> {
  const out = new Map<string, number>();
  for (const i of items ?? []) {
    const v = Math.max(0, Number(i.dpCreditLeft) || 0);
    if (v > 0 && !out.has(i.orderId)) out.set(i.orderId, v);
  }
  return out;
}

/**
 * Bagi DP per pesanan ke dua kolom: Sudah Ready DULU — tagihan barang terbit
 * dari barang ready, di sanalah server memasang Kredit DP — sisanya ke Belum
 * Ready. Dihitung dari daftar Sudah Ready PENUH (bukan hasil pencarian) supaya
 * bagian Belum Ready tak berubah-ubah mengikuti kata kunci. Tanpa pembagian
 * ini pesanan yang itemnya terbelah dua kolom terpotong DP-nya dua kali.
 */
export function splitCartDpCredit(
  creditByOrder: ReadonlyMap<string, number>,
  readyItems: ReadonlyArray<CartColumnSummaryItem>
): { ready: Map<string, number>; belumReady: Map<string, number> } {
  const readyUnpaid = new Map<string, number>();
  for (const item of readyItems ?? []) {
    if (!item.orderId) continue;
    const rp = itemUnpaidRupiah(item);
    if (rp > 0) readyUnpaid.set(item.orderId, (readyUnpaid.get(item.orderId) ?? 0) + rp);
  }
  const ready = new Map<string, number>();
  const belumReady = new Map<string, number>();
  for (const [orderId, credit] of creditByOrder) {
    const toReady = Math.min(credit, readyUnpaid.get(orderId) ?? 0);
    if (toReady > 0) ready.set(orderId, toReady);
    if (credit - toReady > 0) belumReady.set(orderId, credit - toReady);
  }
  return { ready, belumReady };
}

/**
 * Keterangan potongan di bawah total — DP terbayar dan/atau bayar sebagian
 * nota barang (pemilik 2026-10-24). `null` bila tak ada yang dipotong.
 */
export function cartColumnDpNote(s: CartColumnSummary): string | null {
  const parts = [
    s.dpDeducted > 0 ? `DP terbayar ${formatPrice(s.dpDeducted)}` : null,
    (s.partialPaidDeducted ?? 0) > 0
      ? `bayar sebagian ${formatPrice(s.partialPaidDeducted)}`
      : null
  ].filter(Boolean);
  return parts.length ? `sudah dipotong ${parts.join(" + ")}` : null;
}

/**
 * Tiga segmen ringkasan. Kotak di /orders menyambung dua segmen pertama
 * dengan " | " (masing-masing utuh, supaya di HP patahnya di pemisah, bukan
 * di tengah "25 pcs lunas") dan menaruh segmen total di barisnya sendiri.
 *
 * Pcs harga menyusul disebut terang: "Rp 1.250.000 + 3 pcs harga menyusul".
 * Bila SEMUA pcs belum lunas belum berharga, rupiahnya dihilangkan — "Rp 0"
 * di situ terbaca "tak ada tagihan", padahal harganya saja yang belum ada.
 */
export function cartColumnSummarySegments(
  s: CartColumnSummary
): [string, string, string] {
  const menyusul = `${s.unpricedPcs} pcs harga menyusul`;
  const total =
    s.unpricedPcs > 0 && s.unpaidTotal === 0
      ? menyusul
      : `${formatPrice(s.unpaidTotal)}${s.unpricedPcs > 0 ? ` + ${menyusul}` : ""}`;
  return [
    `${s.products} produk ${s.pcs} pcs`,
    `${s.paidPcs} pcs lunas ${s.unpaidPcs} pcs belum lunas`,
    `total belum lunas = ${total}`
  ];
}

/** Pill tambahan "Sudah dibuat nota" untuk item yang ada di nota aktif. */
export function deriveTagihanBadge(
  inActiveInvoice: boolean | null | undefined
): "dibuat_nota" | null {
  return inActiveInvoice ? "dibuat_nota" : null;
}

// ─── Nota yang memuat sebuah item keranjang ───────────────────────────────────
//
// Keranjang dulu hanya tahu SATU hal tentang nota: "item ini ada di nota aktif"
// (boolean `inActiveInvoice`) — cukup untuk menggerbang tombol, tapi tak pernah
// menyebut nota MANA. Begitu notanya lunas penandanya bahkan hilang sama
// sekali, jadi baik admin maupun pembeli tak punya jalan dari sebuah varian ke
// tagihan yang menagihnya. Refs di bawah menutup itu: identitas nota per item.

/** Nota yang memuat sebuah item keranjang (satu baris `invoice_lines` aktif). */
export type CartInvoiceRef = {
  id: string;
  invoiceNo: number | null;
  invoiceCode: string | null;
  status: InvoiceStatus;
  kind: InvoiceKind;
  /**
   * Tempo bayar nota (mig 4260). NULL = nota ini tak pernah kedaluwarsa —
   * item yang ditahannya karena itu juga tak punya tenggat yang berlaku.
   */
  dueAt: string | null;
  /**
   * Nota ini sedang menunggu admin memproses bukti transfer? Menentukan APA
   * yang boleh dijanjikan label tempo saat notanya lewat tempo: sapuan
   * `void_overdue_invoices_internal` MELEWATI nota yang punya setoran
   * `pending`, jadi menjanjikan "akan dibatalkan otomatis" di situ adalah
   * janji yang tak akan ditepati.
   *
   * `null` = pemanggilnya TIDAK menanyakannya (embed `invoice_payments` absen)
   * — beda dari `false` (ditanya, memang tak ada), persis pembedaan yang sudah
   * dipakai `invoiceMoneyUntouched` di bawah.
   */
  awaitingPaymentConfirmation: boolean | null;
  /**
   * Nota ini "Tagihan + Kirim" yang niat kirimnya masih menunggu lunas
   * (`shipment_intents.status = 'pending'`)? Pcs-nya berangkat lewat nota itu
   * (otomatis saat lunas, atau tombol Kirim Sekarang) — Request Kirim atas pcs
   * yang sama menagih ongkirnya dua kali (uji nyata S05 T1). `null` = embed
   * `shipment_intents` tak diminta pemanggil, bukan "tidak ada".
   */
  shipPending: boolean | null;
  /**
   * Ekspedisi niat kirim yang menunggu lunas (chip keranjang "Kirim otomatis
   * saat lunas · JNE REG", uji nyata S02 T8). Absen/null = tak diketahui.
   */
  shipExpedition?: string | null;
};

/**
 * Status nota yang MENGGATE aksi keranjang (`inActiveInvoice`): satu item hanya
 * boleh berada di SATU nota aktif, jadi selama nota ini hidup item tak bisa
 * dinotakan/ditandai-lunas lagi. JANGAN tambahkan `paid` ke sini — nota lunas
 * tidak mengunci apa pun (sisa pcs-nya boleh dinotakan lagi, mig 3690).
 */
export const CART_INVOICE_ACTIVE_STATUSES: InvoiceStatus[] = [
  "pending_acc",
  "open",
  "partial"
];

/**
 * Status nota yang DITAMPILKAN nomornya di keranjang = aktif + `paid`. Nota
 * lunas sengaja ikut: pertanyaan "varian ini masuk tagihan mana" justru paling
 * sering muncul setelah dibayar. `void`/`rejected` TIDAK ikut — nota mati
 * bukan jawaban, dan menampilkannya membuat pembeli mengejar tagihan yang tak
 * pernah perlu dibayar. Nota `paid` masih disaring sekali lagi PER BARIS oleh
 * `cartInvoiceRefsForRow`: baris yang tak memegang pcs lunas tak memajangnya.
 */
export const CART_INVOICE_VISIBLE_STATUSES: InvoiceStatus[] = [
  ...CART_INVOICE_ACTIVE_STATUSES,
  "paid"
];

/** Urutan tampil: nota yang masih menagih dulu, baru yang sudah lunas. */
function invoiceRefRank(status: InvoiceStatus): number {
  return CART_INVOICE_ACTIVE_STATUSES.includes(status) ? 0 : 1;
}

/**
 * Nota ini BELUM TERSENTUH UANG sama sekali? Cermin klien dari predikat yang
 * dipakai `release_invoice_pcs_for_item` (mig 4880) dan
 * `void_overdue_invoices_internal` — hanya nota semacam ini yang boleh
 * dikecilkan oleh pembatalan sebagian pcs.
 *
 * FAIL-CLOSED SAAT KOLOMNYA ABSEN. Pemanggil yang tidak menyertakan kolom uang
 * (jalur pembeli `data.ts`) akan memulangkan `undefined` untuk ketiganya, dan
 * `undefined ?? 0 === 0` akan membaca "tidak ada uang" untuk nota yang mungkin
 * sudah lunas — kelas kesalahan yang sama dengan `count: null` yang menelan
 * error. Karena itu ketiga kolom WAJIB hadir sebagai angka; kalau tidak,
 * jawabannya `false`.
 */
/**
 * Nota ini menunggu admin memproses bukti transfer (`invoice_payments`
 * `pending`)? Selama jawabannya `true`, sapuan tempo melewatinya — gerbang
 * `not exists (… status in ('pending','confirmed'))` di
 * `void_overdue_invoices_internal` (mig 4260).
 *
 * SENGAJA hanya `pending`, bukan `pending`/`confirmed` seperti
 * `invoiceMoneyUntouched`: yang ditanyakan di sini "apakah ada yang sedang
 * ditunggu dari admin", bukan "apakah nota ini pernah tersentuh uang". Setoran
 * `confirmed` pada nota yang masih `open` (hasil `admin_unmark_invoice_paid`,
 * mig 2092) bukan sesuatu yang pembeli tunggu.
 *
 * `null` = embed-nya absen → pemanggil tak boleh menyimpulkan apa pun.
 */
export function invoiceAwaitingPaymentConfirmation(inv: {
  invoice_payments?: unknown;
}): boolean | null {
  const pay = inv?.invoice_payments;
  if (!Array.isArray(pay)) return null;
  return pay.some((p: any) => p?.status === "pending");
}

/** `shipment_intents` di embed nota → ada niat kirim `pending`? `null` = tak diminta. */
export function invoiceShipPending(inv: { shipment_intents?: unknown }): boolean | null {
  const list = inv?.shipment_intents;
  if (!Array.isArray(list)) return null;
  return list.some((i: any) => i?.status === "pending");
}

/** Ekspedisi niat kirim `pending` di embed nota (null = tak ada / tak diminta). */
export function invoiceShipPendingExpedition(inv: { shipment_intents?: unknown }): string | null {
  const list = inv?.shipment_intents;
  if (!Array.isArray(list)) return null;
  const pending = list.find((i: any) => i?.status === "pending");
  const exp = typeof pending?.expedition === "string" ? pending.expedition.trim() : "";
  return exp || null;
}

/** Teks chip baris keranjang untuk pcs yang ikut Tagihan + Kirim menunggu lunas. */
export function shipPendingChipText(expedition: string | null | undefined): string {
  const exp = (expedition ?? "").trim();
  return exp ? `Kirim otomatis saat lunas · ${exp}` : "Kirim otomatis saat lunas";
}

/**
 * Nota "Tagihan + Kirim" menunggu lunas yang memegang baris keranjang ini, bila
 * ada. Pcs baris itu tak boleh ikut Request Kirim — server menolaknya
 * (`create_shipment_request`, migrasi `kirim_niat_sebelum_lunas`).
 */
export function shipPendingInvoiceRef<T extends { shipPending: boolean | null }>(
  refs: readonly T[] | null | undefined
): T | null {
  return (refs ?? []).find((r) => r.shipPending === true) ?? null;
}

function invoiceMoneyUntouched(inv: any): boolean {
  const status = inv?.status as InvoiceStatus | undefined;
  if (status !== "pending_acc" && status !== "open") return false;

  const nol = (v: unknown) => typeof v === "number" && v === 0;
  const angka = (v: unknown) =>
    typeof v === "number" ? v : typeof v === "string" ? Number(v) : NaN;

  // `numeric` PostgREST datang sebagai string; string yang bukan angka tetap
  // gagal (NaN !== 0), jadi tetap fail-closed.
  if (!nol(angka(inv?.amount_paid))) return false;
  if (!nol(angka(inv?.balance_applied))) return false;
  if (!nol(angka(inv?.refunded_amount))) return false;

  // Setoran pending/confirmed = uang sudah bergerak walau `amount_paid` nol.
  // Embed absen (`undefined`) BERBEDA dari embed kosong (`[]`): yang pertama
  // berarti pemanggilnya tak menanyakannya → fail-closed.
  const pay = inv?.invoice_payments;
  if (!Array.isArray(pay)) return false;
  if (pay.some((p: any) => p?.status === "pending" || p?.status === "confirmed"))
    return false;

  return true;
}

/**
 * Susun peta item → nota dari baris `invoice_lines` (sudah difilter aktif +
 * status terlihat di query). Memulangkan SEKALIGUS `activeItemIds` supaya
 * `inActiveInvoice` dan daftar notanya tak pernah bisa menyimpang: keduanya
 * lahir dari baris yang SAMA, dari satu definisi status yang sama.
 *
 * Embed PostgREST to-one bisa datang sebagai objek MAUPUN array (tergantung
 * bentuk relasi yang disimpulkan) — keduanya diterima; baris tanpa nota
 * dilewati alih-alih melempar.
 *
 * `releasableByItem` (mig 4880) = bagian dari `reservedByItem` yang duduk di
 * nota BELUM TERSENTUH UANG, jadi pembatalan sebagian boleh melepasnya dengan
 * mengecilkan notanya. Selalu ⊆ `reservedByItem`, dan 0 untuk pemanggil yang
 * tak menyertakan kolom uang (lihat `invoiceMoneyUntouched`).
 *
 * `billedByItem` (mig 8000) = KLAIM MENTAH per baris di tagihan barang hidup:
 * baris `product_item` di nota `kind='product'` berstatus open/partial/paid
 * (`pending_acc` belum menagih), `qty` NULL = 0.
 *
 * `reservedByItem` dan `billedByItem` adalah saldo PER BARIS. Sejak mig 8340
 * keduanya WAJIB lewat `itemFamilyOpen.ts` sebelum dipakai — `dpBilledQtyByItem`
 * (DP, cermin `compute_order_dp_due`) dan `familyReservedQtyByItem` (pcs bebas,
 * cermin `order_item_free_pcs`): pecah-baris bisa meninggalkan klaim di baris
 * SUMBER yang menutup pcs pecahannya, dan saldo per baris yang dijepit nol
 * membuang kelebihan itu (DP membengkak, pcs yang sama ditawarkan ditagih lagi).
 */
export function buildCartInvoiceIndex(rows: readonly any[]): {
  refsByItem: Map<string, CartInvoiceRef[]>;
  activeItemIds: Set<string>;
  reservedByItem: Map<string, number>;
  releasableByItem: Map<string, number>;
  billedByItem: Map<string, number>;
} {
  const refsByItem = new Map<string, CartInvoiceRef[]>();
  const activeItemIds = new Set<string>();
  const reservedByItem = new Map<string, number>();
  const releasableByItem = new Map<string, number>();
  const billedByItem = new Map<string, number>();

  for (const row of rows ?? []) {
    const itemId = row?.order_item_id as string | null | undefined;
    if (!itemId) continue;

    const embed = row?.invoices;
    const inv = Array.isArray(embed) ? embed[0] : embed;
    if (!inv?.id) continue;

    const status = inv.status as InvoiceStatus;
    if (!CART_INVOICE_VISIBLE_STATUSES.includes(status)) continue;
    if (
      row?.kind === "product_item" &&
      (inv.kind ?? "product") === "product" &&
      (status === "open" || status === "partial" || status === "paid")
    ) {
      const lineQty = Number(row?.qty);
      billedByItem.set(
        itemId,
        (billedByItem.get(itemId) ?? 0) +
          (Number.isFinite(lineQty) ? Math.max(0, Math.floor(lineQty)) : 0)
      );
    }
    if (CART_INVOICE_ACTIVE_STATUSES.includes(status)) {
      activeItemIds.add(itemId);
      // Cermin `order_item_free_pcs` (mig 4810). Baris nota `partial` SUDAH
      // menyumbangkan `paid_qty`-nya ke `order_items.paid_qty` lewat
      // `apply_invoice_allocation`, jadi yang "dipesan" tinggal selisihnya —
      // menjumlah `qty` penuh akan menghitung pcs yang sama dua kali.
      // Baris kind non-`product_item` tak pernah memesan pcs; `qty` NULL
      // (baris warisan) diperlakukan fail-closed lewat penjumlahan di
      // `itemFreePcs`, bukan di sini.
      if (row?.kind == null || row.kind === "product_item") {
        const lineQty = Number(row?.qty);
        const linePaid = Number(row?.paid_qty);
        const reserved = Math.max(
          0,
          (Number.isFinite(lineQty) ? Math.floor(lineQty) : 0) -
            (Number.isFinite(linePaid) ? Math.floor(linePaid) : 0)
        );
        reservedByItem.set(itemId, (reservedByItem.get(itemId) ?? 0) + reserved);
        if (invoiceMoneyUntouched(inv)) {
          releasableByItem.set(
            itemId,
            (releasableByItem.get(itemId) ?? 0) + reserved
          );
        }
      }
    }

    const list = refsByItem.get(itemId) ?? [];
    // Satu nota bisa memuat >1 baris untuk item yang sama (hasil sunting);
    // yang ditampilkan identitas notanya, jadi cukup sekali.
    if (list.some((r) => r.id === inv.id)) continue;
    list.push({
      id: inv.id as string,
      invoiceNo: inv.invoice_no != null ? Number(inv.invoice_no) : null,
      invoiceCode: (inv.invoice_code as string | null) ?? null,
      status,
      kind: (inv.kind ?? "product") as InvoiceKind,
      dueAt: (inv.due_at as string | null) ?? null,
      awaitingPaymentConfirmation: invoiceAwaitingPaymentConfirmation(inv),
      shipPending: invoiceShipPending(inv),
      shipExpedition: invoiceShipPendingExpedition(inv)
    });
    refsByItem.set(itemId, list);
  }

  for (const list of refsByItem.values()) {
    list.sort(
      (a, b) =>
        invoiceRefRank(a.status) - invoiceRefRank(b.status) ||
        (a.invoiceNo ?? 0) - (b.invoiceNo ?? 0) ||
        a.id.localeCompare(b.id)
    );
  }

  return { refsByItem, activeItemIds, reservedByItem, releasableByItem, billedByItem };
}

/**
 * Nota yang DIPAJANG di satu baris keranjang — saringan per-baris di atas
 * `refsByItem` milik `buildCartInvoiceIndex`. Nota `paid` hanya dipajang bila
 * baris itu SENDIRI masih memegang pcs lunas (`paidQty > 0`).
 *
 * Sebabnya pecah-baris: saat pcs lunas dikirim, `create_shipment_request_
 * internal` (dan dua pemindah lain, bughunt B2 §"Pertanyaan 4") memindahkan
 * pcs itu ke baris ANAK, sementara `invoice_lines.order_item_id` tetap menunjuk
 * baris INDUK. Baris anak `requested_shipment`/`shipped` tak tampil di
 * keranjang, jadi tanpa saringan ini baris induk yang tinggal memegang pcs
 * BELUM ditagih memajang "Belum Lunas" berdampingan dengan "Nota … ✓" milik pcs
 * yang sudah berangkat — terbaca "notanya lunas tapi barangnya belum" (zzhomey
 * CO-2609260064: nota 9 pcs lunas, 1 pcs sisa tak pernah ditagih; 7 baris
 * serupa di PROD 2026-09-28).
 *
 * Aman: baris ber-nota-lunas hanya bisa ber-`paid_qty = 0` lewat pemindah
 * pecah-baris — jalur turun lain mencabut status `paid` notanya (Batalkan
 * Pelunasan) atau memasang `cancelled_at` baris notanya (cancel), dan keduanya
 * sudah tersaring sebelum sampai sini. Nota AKTIF selalu lolos: ia menagih pcs
 * yang masih di baris ini, dan `keepDeadlineView` membacanya.
 *
 * Batas yang disengaja: baris yang masih memegang pcs lunas memajang SEMUA nota
 * lunasnya, termasuk yang pcs-nya sudah pindah — sesudah pecah-baris, pcs mana
 * milik nota mana tak bisa dipastikan per baris.
 */
export function cartInvoiceRefsForRow<T extends { status: InvoiceStatus }>(
  refs: readonly T[] | null | undefined,
  paidQty: number | null | undefined
): T[] {
  const list = refs ?? [];
  const paid = Math.floor(Number(paidQty ?? 0));
  if (Number.isFinite(paid) && paid > 0) return [...list];
  return list.filter((r) => r.status !== "paid");
}

/**
 * Pcs sebuah item yang masih BISA DINOTAKAN — cermin TS `order_item_free_pcs`
 * (mig 4810), satu-satunya definisi yang boleh dipakai gerbang nota.
 *
 * Menggantikan gerbang lama `inActiveInvoice` (boolean). Sejak guard "satu item
 * = satu nota aktif" dicabut, KEBERADAAN nota aktif tidak lagi menghalangi apa
 * pun — yang menghalangi adalah habisnya pcs. `inActiveInvoice` TETAP hidup
 * untuk hal-hal yang memang per-item (pil nota, hint tenggat hangus, gerbang
 * cancel & Batal Tandai Lunas); jangan tukar keduanya.
 */
export function itemFreePcs(item: {
  qty: number;
  paidQty: number | null | undefined;
  reservedQty?: number | null;
}): number {
  const qty = Math.floor(item.qty);
  const paid = Math.floor(item.paidQty ?? 0);
  const reserved = Math.floor(item.reservedQty ?? 0);
  return Math.max(0, qty - paid - reserved);
}

/** Status order yang dianggap "Belum Ready" (PO masih berjalan). */
export const CART_BELUM_READY_ORDER_STATUSES: string[] = [
  "Menunggu",
  "Diproses",
  "Produksi",
  "Belum Ready"
];

// ─── Tahap PO — isi badge pojok kanan atas kartu "Belum Ready" ───────────────
//
// Badge itu dulu memajang `orders.status` mentah ("Produksi"), sementara tahap
// PO dipajang sebagai chip terpisah tepat di bawahnya ("Sedang Produksi") —
// terbaca kembar, dan chipnya sempat dibuang seluruhnya. Sekarang tahap PO yang
// MENGISI badge itu, dan status order tinggal jadi cadangan.
//
// KENAPA TAHAP PO YANG MENANG. Kedua kolom digerakkan RPC yang sama tapi tidak
// sinkron: submit produksi per-varian menaikkan `products.status` untuk SELURUH
// produk sementara hanya order yang variannya disubmit yang berubah (jadi order
// saudara tertinggal "Menunggu" padahal barangnya sudah diproduksi), dan
// `mark_variants_production_failed` (mig 3960) menyetel produk `GAGAL`
// sementara ordernya tetap "Produksi". `orders.status` di kartu ini juga cuma
// pernah bernilai empat (lihat CART_BELUM_READY_ORDER_STATUSES di atas),
// sedangkan tahap PO membedakan delapan keadaan.
//
// HANYA LABEL, bukan warna. Warnanya diambil `getStatusTone` (products.ts) yang
// SUDAH memetakan kedelapan nilai `preorder_status` ke pil pekat berhuruf
// putih. Versi lama helper ini membawa peta warna pastelnya sendiri; peta itu
// sengaja TIDAK dihidupkan lagi — dua sumber warna status yang bisa menyimpang
// adalah persis jenis kembar yang baru saja dibereskan.

/**
 * Label tahap PO sebuah kartu pesanan Belum Ready, dari `products.status`
 * (enum `preorder_status` — lihat docs/ENUM.md). `null` = tak dikenal (pesanan
 * tamu tanpa snapshot status produk, atau nilai di luar enum) → pemanggil jatuh
 * ke badge status order supaya pojoknya tak pernah jadi pil kosong.
 */
export function poStageLabel(
  productStatus: string | null | undefined,
  opts?: { isManual?: boolean }
): string | null {
  switch (productStatus) {
    // Sengaja "Open PO" saja, bukan "Open PO · bisa diedit" seperti versi chip
    // lama: slot badge ber-`shrink-0`, jadi label panjang langsung memakan
    // lebar nama produk di sebelahnya. Bahwa PO ini bisa diedit sudah terbaca
    // dari tombol Edit yang memang muncul di kartunya.
    case "OPEN PO":
      return "Open PO";
    case "CLOSED PO":
      return "PO Ditutup";
    case "PROSES INPUT":
      return "Persiapan Produksi";
    // Produk Non Web Belum Ready lahir berstatus PROSES PRODUKSI (mig 6370),
    // padahal tak ada produksi — barangnya ditunggu datang (bughunt D8-18).
    case "PROSES PRODUKSI":
      return opts?.isManual ? "Menunggu Barang" : "Sedang Produksi";
    // Kartu masih di Belum Ready padahal produknya sudah (sebagian) datang =
    // pcs pesanan ini belum kebagian alokasi — beri tahu barangnya bertahap.
    case "READY SEBAGIAN":
      return "Datang Bertahap";
    case "READY LENGKAP":
    case "PENDISTRIBUSIAN":
      return "Barang Datang";
    case "GAGAL":
      return "Gagal Produksi";
    default:
      return null;
  }
}

/**
 * Baris preorder yang boleh masuk paket SEBELUM barangnya datang (mig 7760)
 * — cermin PERSIS `order_item_awaiting_goods`: preorder, keep NULL, tak sedang
 * di paket lain, pesanan aktif. Sejak mig 7890 baris Open PO / belum disubmit
 * (`item_status` 'Menunggu') IKUT memenuhi: `submit_closed_po_to_production`
 * kini menyeret baris yang sudah di paket (pecahannya tetap di paket), rekap
 * Closed PO menghitungnya, dan paketnya menunggu di Dikemas · Belum Ready
 * sampai PO ditutup, disubmit, dan barangnya datang. `itemStatus` tak lagi
 * menentukan (dibiarkan di tanda tangan untuk pemanggil lama).
 *
 * Kolom yang ABSEN dibaca fail-closed (tidak memenuhi): server tetap
 * otoritatif, dan tombol yang menyala untuk baris yang pasti ditolak lebih
 * buruk daripada tombol mati. Pemanggil yang ingin menawarkan aksi ini wajib
 * membawa `productType` dan `orderStatus`.
 */
export function isAwaitingGoodsItem(item: {
  keepStatus: string | null | undefined;
  productType?: string | null;
  orderStatus?: string | null;
  itemStatus?: string | null;
  shipmentRequestId?: string | null;
}): boolean {
  if (item.keepStatus != null) return false;
  if (item.productType !== "preorder") return false;
  if (item.shipmentRequestId) return false;
  if (!CART_BELUM_READY_ORDER_STATUSES.includes(item.orderStatus ?? "")) {
    return false;
  }
  // mig 7890: baris 'Menunggu' (Open PO / belum disubmit) ikut memenuhi.
  return true;
}

/**
 * Pcs yang boleh diajukan kirim dari sebuah baris item = pcs yang lunas
 * (cermin guard create_shipment_request v2: keep unpaid/paid + ship_qty ≤
 * paid_qty). Sejak mig 7760 baris Belum Ready yang sudah disubmit ke produksi
 * (`isAwaitingGoodsItem`) ikut: pcs lunasnya boleh diminta kirim sekarang dan
 * paketnya menunggu barang di kolom Dikemas.
 */
export function shippableQty(input: {
  keepStatus: string | null | undefined;
  paidQty: number | null | undefined;
  qty: number;
  productType?: string | null;
  orderStatus?: string | null;
  itemStatus?: string | null;
  shipmentRequestId?: string | null;
}): number {
  const keepReady =
    input.keepStatus === "unpaid_keep" || input.keepStatus === "paid_keep";
  if (!keepReady && !isAwaitingGoodsItem(input)) {
    return 0;
  }
  return Math.max(0, Math.min(input.qty, input.paidQty ?? 0));
}

/**
 * Jepit qty kirim satu baris ke batas atasnya.
 *
 * Lantainya BERGANTUNG pada atapnya, dan itu bukan detail: rumus lama
 * `Math.max(1, Math.min(q, max))` membatalkan atapnya sendiri saat `max` = 0 —
 * hasilnya **1**, yaitu satu pcs untuk baris yang batas atasnya nol. Hari ini
 * belum bisa dicapai karena penyaring kelayakan (`eligibleFor`, `eligibleIds`)
 * membuang baris ber-`max` 0 lebih dulu, tapi lantai yang tak melihat atap
 * adalah gerbang yang menunggu penyaring di atasnya berubah.
 *
 * `max` 0 ⇒ 0. Selain itu minimal 1 pcs, maksimal `max`.
 */
export function clampShipQty(
  qty: number | null | undefined,
  max: number
): number {
  if (!Number.isFinite(max) || max <= 0) return 0;
  const q = Number.isFinite(qty) ? Number(qty) : max;
  return Math.max(1, Math.min(q, max));
}

/**
 * Item yang memenuhi syarat aksi gabungan "Nota + Kirim" (mig 157): harus
 * billable (masih ada pcs BEBAS) DAN boleh masuk paket — keep belum bayar
 * (siap kirim setelah lunas) ATAU, sejak mig 7760, baris Belum Ready yang
 * sudah disubmit ke produksi (`isAwaitingGoodsItem`; paketnya lahir saat nota
 * lunas dan menunggu barang; sejak mig 7890 termasuk baris Open PO / belum
 * disubmit). Cermin guard server `request_invoice_with_shipment`.
 *
 * Sejak mig 4810/4850 gerbangnya PCS, bukan `inActiveInvoice`: item yang
 * separuh pcs-nya sudah dinotakan tetap boleh dinotakan untuk sisanya.
 */
export function comboEligible(input: {
  keepStatus: string | null | undefined;
  paidQty: number | null | undefined;
  qty: number;
  reservedQty?: number | null;
  productType?: string | null;
  orderStatus?: string | null;
  itemStatus?: string | null;
  shipmentRequestId?: string | null;
}): boolean {
  if (input.keepStatus !== "unpaid_keep" && !isAwaitingGoodsItem(input)) {
    return false;
  }
  return itemFreePcs(input) > 0;
}

// ─── Kesiapan barang di dalam PAKET (mig 7760) ───────────────────────────────
//
// Sejak mig 7760 sebuah baris paket (`keep_status` 'requested_shipment') punya
// dua sumbu: keanggotaan paket dan KESIAPAN BARANG — `ready_at` (mig 7040):
// NULL = barangnya belum datang, paket menunggu di Dikemas. Paket yang sudah
// berangkat (`shipped`) selalu ready. Baris batal tak dihitung. Cermin SQL
// `shipment_goods_readiness`.

export type GoodsReadiness = "ready_lengkap" | "ready_sebagian" | "belum_ready";

export type GoodsReadinessSummary = {
  state: GoodsReadiness;
  totalPcs: number;
  readyPcs: number;
  notReadyPcs: number;
};

/** Dua kolom Dikemas: paket yang barangnya lengkap vs yang masih menunggu. */
export type GoodsReadyColumn = "ready_lengkap" | "belum_ready";

export const GOODS_READY_COLUMN_LABEL: Record<GoodsReadyColumn, string> = {
  ready_lengkap: "Ready Lengkap",
  belum_ready: "Belum Ready"
};

/** Barang satu baris paket sudah ada (`ready_at` terisi) atau sudah berangkat. */
export function isItemGoodsReady(item: {
  readyAt?: string | null;
  keepStatus?: string | null;
}): boolean {
  return item.keepStatus === "shipped" || item.readyAt != null;
}

/**
 * Kesiapan barang sebuah paket dari baris-barisnya. Paket kosong = lengkap
 * (tak ada barang yang ditunggu). Baris `cancelled` diabaikan.
 */
export function shipmentGoodsReadiness(
  items: ReadonlyArray<{
    qty: number | null | undefined;
    readyAt?: string | null;
    keepStatus?: string | null;
  }>
): GoodsReadinessSummary {
  let totalPcs = 0;
  let readyPcs = 0;
  for (const it of items) {
    if (it.keepStatus === "cancelled") continue;
    const q = Math.max(0, Math.floor(Number(it.qty) || 0));
    totalPcs += q;
    if (isItemGoodsReady(it)) readyPcs += q;
  }
  const state: GoodsReadiness =
    readyPcs >= totalPcs
      ? "ready_lengkap"
      : readyPcs === 0
        ? "belum_ready"
        : "ready_sebagian";
  return { state, totalPcs, readyPcs, notReadyPcs: Math.max(0, totalPcs - readyPcs) };
}

/** Kolom Dikemas tempat paket duduk: Ready Sebagian ikut "Belum Ready". */
export function goodsReadyColumn(state: GoodsReadiness): GoodsReadyColumn {
  return state === "ready_lengkap" ? "ready_lengkap" : "belum_ready";
}

/** Chip kesiapan paket (board, detail admin, kartu & detail pembeli). */
export function goodsReadinessMeta(
  s: GoodsReadinessSummary
): { label: string; color: string } {
  switch (s.state) {
    case "ready_lengkap":
      return { label: "Ready Lengkap", color: "bg-leaf/15 text-leaf" };
    case "ready_sebagian":
      return {
        label: `Ready Sebagian · ${s.readyPcs}/${s.totalPcs} pcs`,
        color: "bg-amber-100 text-amber-700"
      };
    default:
      return { label: "Belum Ready", color: "bg-marigold/15 text-marigold" };
  }
}

/**
 * Chip umur tunggu paket Belum Ready (mig 7840) — dihitung sejak paket dibuat
 * (hari kalender penuh). null = tanggal tak terbaca; 0 hari = "sejak hari ini".
 */
export function goodsWaitingLabel(
  createdAt: string | null | undefined,
  now: Date = new Date()
): string | null {
  if (!createdAt) return null;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return null;
  const days = Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
  return days === 0 ? "Menunggu barang sejak hari ini" : `Menunggu barang ${days} hari`;
}

/** Badge per baris isi paket: barangnya sudah ada atau belum. */
export function itemGoodsBadge(item: {
  readyAt?: string | null;
  keepStatus?: string | null;
}): { label: string; color: string } {
  return isItemGoodsReady(item)
    ? { label: "Ready", color: "bg-leaf/15 text-leaf" }
    : { label: "Belum Ready", color: "bg-marigold/15 text-marigold" };
}

/**
 * Label satu baris pesanan yang sudah di paket / dikirim untuk daftar "Item
 * terkunci" editor Open PO (audit paket menunggu barang, mig 7890/7910).
 * Menyebut KEADAANNYA supaya admin tahu kenapa barisnya tak ikut diedit dan
 * ke mana mengubahnya (Revisi Isi Paket), bukan sekadar nama varian.
 */
export function packagedRowLockLabel(row: {
  variantName: string;
  qty: number;
  keepStatus: string | null;
  readyAt?: string | null;
}): string {
  const state =
    row.keepStatus === "shipped"
      ? "sudah dikirim"
      : row.readyAt
        ? "di paket, ready"
        : "di paket, menunggu barang";
  return `${row.variantName} ×${row.qty} (${state})`;
}

/**
 * Pembeli boleh mengajukan NOTA untuk baris ini: masih ada pcs BEBAS (mig
 * 4810 — bukan lagi "belum ada di nota aktif") dan barisnya keep Ready yang
 * belum bayar ATAU item PO yang masih proses. Cermin guard `request_invoice`;
 * server tetap memvalidasi. Satu definisi untuk keranjang (`OrdersClient`) dan
 * halaman Buat Nota (`CustomerActionData`) — dulu tersalin di beberapa tempat,
 * dan checkbox baris Belum Ready tertinggal di aturan lama `!inActiveInvoice`.
 */
export function isCustomerBillable(item: {
  keepStatus: string | null | undefined;
  productType: string;
  paidQty: number | null | undefined;
  qty: number;
  reservedQty?: number | null;
}): boolean {
  return (
    itemFreePcs(item) > 0 &&
    (item.keepStatus === "unpaid_keep" ||
      (item.keepStatus == null && item.productType === "preorder"))
  );
}

/**
 * Baris keranjang pembeli yang BOLEH DICENTANG: bisa dinotakan, atau sudah
 * punya pcs lunas untuk dikirim. Gerbang SATU-SATUNYA untuk checkbox baris,
 * centang kepala kartu, dan chip "Semua" — bila syaratnya berbeda, "Semua"
 * mencentang baris yang tak punya checkbox untuk dilepas lagi.
 */
export function isCustomerPickable(item: {
  keepStatus: string | null | undefined;
  productType: string;
  paidQty: number | null | undefined;
  qty: number;
  reservedQty?: number | null;
}): boolean {
  return isCustomerBillable(item) || shippableQty(item) > 0;
}

// ─── Centang sekelompok item: chip "Semua" & kepala kartu ────────────────────
// Dipakai BERSAMA keranjang pembeli (`OrdersClient`, nilai = qty kirim) dan
// keranjang admin (`AdminCartClient`, nilai = `true`). Pemanggil hanya mengirim
// item yang BOLEH dicentang di layarnya — item batal / tanpa aksi tak pernah
// ikut tercentang.

/** Keadaan centang sekelompok item: kosong / sebagian (strip) / semua. */
export type CartPickState = "none" | "some" | "all";

/**
 * Item dihitung tercentang bila kuncinya ADA (`!== undefined`), bukan bila
 * nilainya truthy — pilihan pembeli bernilai qty kirim. Kelompok tanpa item
 * yang bisa dipilih = "none" (bukan "all" yang hampa).
 */
export function cartPickState(
  itemIds: readonly string[],
  selected: Readonly<Record<string, unknown>>
): CartPickState {
  let picked = 0;
  for (const id of itemIds) if (selected[id] !== undefined) picked += 1;
  if (picked === 0) return "none";
  return picked === itemIds.length ? "all" : "some";
}

/**
 * Ketuk centang kepala kartu: kelompok yang SUDAH tercentang semua dilepas;
 * selain itu (kosong / sebagian) dilengkapi — item yang belum tercentang
 * mendapat `value` bawaannya, yang sudah tercentang MEMPERTAHANKAN nilainya
 * (qty kirim yang sudah diubah pembeli tidak ditimpa). Pilihan di luar
 * kelompok — kartu lain — tak disentuh, dan `selected` tak dimutasi.
 */
export function toggleCartPickGroup<V>(
  selected: Readonly<Record<string, V>>,
  picks: ReadonlyArray<{ id: string; value: V }>
): Record<string, V> {
  const next: Record<string, V> = { ...selected };
  const ids = picks.map((p) => p.id);
  if (cartPickState(ids, selected) === "all") {
    for (const id of ids) delete next[id];
  } else {
    for (const p of picks) if (next[p.id] === undefined) next[p.id] = p.value;
  }
  return next;
}

/**
 * Label badge "Kirim langsung" untuk kartu nota yang punya niat kirim pending
 * (mig 157). Sertakan ekspedisi bila ada.
 */
export function shipmentIntentBadgeLabel(
  recipientName: string | null | undefined,
  expedition: string | null | undefined
): string {
  const to = (recipientName ?? "").trim() || "alamat tujuan";
  const via = (expedition ?? "").trim();
  return via
    ? `Kirim langsung ke ${to} · ${via}`
    : `Kirim langsung ke ${to}`;
}

/**
 * Nama varian default produk TANPA variasi nyata. ProductForm & import membuat
 * satu varian bernama "Standar" untuk produk tanpa grup variasi; begitu pula
 * produk manual (mig 121). Di keranjang label "Standar" tak informatif — kartu
 * cukup tampilkan nama produk + harga, tanpa baris "variasi".
 */
export const VARIANTLESS_VARIANT_NAME = "Standar";

/** True bila item ini produk tanpa variasi nyata (varian default "Standar"). */
export function isVariantlessItem(
  variantName: string | null | undefined
): boolean {
  return (
    (variantName ?? "").trim().toLowerCase() ===
    VARIANTLESS_VARIANT_NAME.toLowerCase()
  );
}

/**
 * Pengganti nama varian "Standar" di daftar item Detail Pengiriman: produk Non
 * Web (`products.is_manual`, mig 121) → "Produk Non Web"; produk katalog tanpa
 * variasi → "Tanpa variasi". Dulu SEMUA baris "Standar" berlabel "Produk
 * manual" — keliru untuk produk katalog tanpa variasi, yang varian tunggalnya
 * juga bernama "Standar". Asal tak diketahui (undefined) = label netral.
 */
export function variantlessItemLabel(isManual: boolean | null | undefined): string {
  return isManual === true ? "Produk Non Web" : "Tanpa variasi";
}

/**
 * Teks notifikasi untuk DITAMPILKAN: " (Standar)" — nama varian default produk
 * tanpa variasi (Non Web maupun katalog) — dibuang. Teksnya dibangun
 * `tag_ready_items_and_check_completion` ("X (Standar) sudah ready…", mesin inti
 * Tandai Ready semua PO, mig 5920); dirapikan di sini alih-alih membuat ulang
 * fungsi sebesar itu hanya demi teks (bughunt D8-18). Varian lain tak disentuh.
 */
export function notificationDisplayText(text: string | null | undefined): string {
  return (text ?? "").split(` (${VARIANTLESS_VARIANT_NAME})`).join("");
}

/**
 * Teks "Produk — Variasi" untuk baris ringkas. Produk tanpa variasi (varian
 * "Standar", mis. produk Non Web mig 121) → cukup nama produk, tanpa " — Standar".
 */
export function productVariantLine(
  productName: string,
  variantName: string | null | undefined
): string {
  return isVariantlessItem(variantName)
    ? productName
    : `${productName} — ${variantName}`;
}

/**
 * Nama varian → kolom "Varian 1/2/3" tabel dokumen cetak. Opsi dipisah "/"
 * (konvensi `buildVariantLabel`/`variantOptionSetKey`); produk tanpa variasi
 * ("Standar") → tanpa varian sama sekali, memakai `isVariantlessItem` yang
 * sama dengan seluruh layar. Lebih dari `slots` opsi → sisanya digabung ke
 * kolom terakhir supaya tak ada opsi yang HILANG dari dokumen.
 *
 * Tinggal di sini, bukan di `invoicePdf.ts` tempatnya lahir: sejak surat jalan
 * memakai tabel bergaya nota (2026-09), dua pencetak berbeda memecah nama
 * varian — dan dua salinan pemecah berarti satu dokumen bisa menaruh "XL" di
 * kolom yang berbeda dari dokumen lain untuk barang yang sama. `invoicePdf`
 * meneruskan ekspor ini apa adanya.
 */
export function splitVariantParts(name: string, slots = 3): string[] {
  const parts = (name ?? "")
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 1 && isVariantlessItem(parts[0])) return [];
  if (parts.length <= slots) return parts;
  return [...parts.slice(0, slots - 1), parts.slice(slots - 1).join(" / ")];
}

// ─── Pencarian keranjang & pengiriman (storefront /orders) ───────────────────
// Cocok substring case-insensitive; query kosong → kembalikan apa adanya
// (cermin gaya searchBoardProducts/filterCartBuyers).

const normalizeSearch = (value: string | null | undefined) =>
  (value ?? "").trim().toLowerCase();

/**
 * Filter item keranjang: cocok pada NAMA PRODUK / ISI VARIASI (variantName) /
 * CATATAN VARIASI (note) / kode order.
 */
export function filterCartItems<
  T extends {
    productName: string;
    variantName: string;
    note?: string | null;
    orderCode?: string;
  }
>(items: T[], query: string): T[] {
  const q = normalizeSearch(query);
  if (!q) return items;
  return items.filter(
    (item) =>
      item.productName.toLowerCase().includes(q) ||
      item.variantName.toLowerCase().includes(q) ||
      normalizeSearch(item.note).includes(q) ||
      normalizeSearch(item.orderCode).includes(q)
  );
}

/**
 * Nomor Pengiriman untuk tampilan: `#` + 8 karakter pertama id (huruf besar) —
 * konvensi yang sama dengan header Detail Pengiriman admin & kode pada label
 * alamat/surat jalan (`shippingLabel.ts`), jadi pembeli dan admin membaca
 * nomor yang identik untuk shipment yang sama.
 */
export function shipmentCode(id: string): string {
  return `#${id.slice(0, 8).toUpperCase()}`;
}

/**
 * Pesan WhatsApp "tanya nomor resi ke admin" dari halaman Detail Pengiriman
 * pembeli.
 *
 * Pembeli MEMANG sudah melihat Nomor Pengiriman (`KRM…`/`#XXXXXXXX`) di
 * layarnya; yang ia kejar adalah **nomor resi** kurir. Karena di percakapan
 * sehari-hari keduanya sama-sama disebut "nomor pengiriman", pesan ini
 * menyebut KEDUA istilah secara eksplisit supaya admin tak salah tangkap:
 * nomor paket dikirim sebagai IDENTITAS (admin bisa mencarinya — kolom
 * Pengiriman board memang mencari kode ini), nomor resi sebagai YANG DITANYA.
 *
 * Kalimatnya menyesuaikan fase, karena pertanyaan yang benar berbeda: paket
 * yang sudah berstatus dikirim tapi resinya kosong itu KEJANGGALAN, sedangkan
 * paket yang masih dikemas belum punya resi karena memang belum berangkat —
 * menuduhnya "resi belum saya terima" akan terbaca seperti komplain salah
 * alamat.
 */
export function buildResiInquiryMessage(
  input: {
    /** Nomor Pengiriman yang TERLIHAT di layar pembeli (kode kustom / `#XXXX`). */
    shipmentCode: string;
    storeName?: string | null;
    /** true = paket sudah berstatus dikirim/selesai. */
    shipped: boolean;
    expedition?: string | null;
    /** ISO tanggal kirim; dilewati bila kosong. */
    shippedAt?: string | null;
    totalPcs?: number | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  const exp = (input.expedition ?? "").trim();

  let tanggal = "";
  if (input.shippedAt) {
    const d = new Date(input.shippedAt);
    if (!Number.isNaN(d.getTime())) {
      tanggal = d.toLocaleDateString("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    }
  }

  return renderWaTemplate(resolveWaTemplate("tanya_resi", overrides), {
    "[nama toko]": (input.storeName ?? "").trim(),
    "[nomor pengiriman]": input.shipmentCode,
    "[baris ekspedisi]": exp ? `Ekspedisi: ${exp}` : "",
    "[baris tanggal kirim]": tanggal ? `Tanggal kirim: ${tanggal}` : "",
    "[baris isi paket]":
      input.totalPcs != null && input.totalPcs > 0
        ? `Isi paket: ${input.totalPcs} pcs`
        : "",
    "[kalimat status paket]": input.shipped
      ? "Paket sudah berstatus DIKIRIM, tapi nomor resinya belum muncul di aplikasi. Boleh dibantu infonya?"
      : "Paket saya masih diproses. Boleh dibantu info nomor resinya setelah dikirim?"
  });
}

/**
 * Pesan WhatsApp "minta ganti alamat" dari halaman Detail Pengiriman PEMBELI.
 *
 * Pembeli TIDAK diberi kemampuan mengubah alamatnya sendiri (mig 4270), dan itu
 * bukan kehati-hatian umum: alamat baru menggeser ongkir, dan ongkir yang
 * bergeser menerbitkan tagihan selisih atau mengembalikan uang ke saldo.
 * Keputusan uang itu milik admin. Yang dibawa pesan ini karena itu hanya bahan
 * yang membuat admin bisa memutuskannya tanpa bertanya balik:
 *
 *  • **Nomor Pengiriman** sebagai identitas — kolom Pengiriman board admin
 *    memang mencari kode ini (`shipmentCodeOrExpr`), jadi admin tinggal
 *    menempelkannya.
 *  • **Alamat yang SEDANG berlaku**, dikutip apa adanya dari snapshot, supaya
 *    pembeli cukup membalas bagian yang berubah dan admin tak perlu menebak
 *    alamat mana yang sedang dipakai.
 *  • **Peringatan ongkir bisa berubah.** Menyembunyikannya berarti pembeli
 *    menerima tagihan selisih yang tak pernah ia duga — persis keluhan yang
 *    membuat fitur ini dibuat. Kalimatnya menyebut dua arah (naik & turun),
 *    bukan hanya yang menagih.
 */
export function buildAddressChangeMessage(
  input: {
    /** Nomor Pengiriman yang TERLIHAT di layar pembeli (kode kustom / `#XXXX`). */
    shipmentCode: string;
    storeName?: string | null;
    /** Baris-baris alamat berjalan; yang kosong dibuang, bukan dicetak kosong. */
    currentAddressLines?: ReadonlyArray<string | null | undefined>;
    expedition?: string | null;
  },
  overrides?: WaTemplateOverrides | null
): string {
  const exp = (input.expedition ?? "").trim();
  const current = (input.currentAddressLines ?? [])
    .map((l) => (l ?? "").trim())
    .filter(Boolean);

  return renderWaTemplate(resolveWaTemplate("ganti_alamat", overrides), {
    "[nama toko]": (input.storeName ?? "").trim(),
    "[nomor pengiriman]": input.shipmentCode,
    "[baris ekspedisi]": exp ? `Ekspedisi: ${exp}` : "",
    "[blok alamat sekarang]": current.length
      ? ["Alamat sekarang:", ...current].join("\n")
      : ""
  });
}

/**
 * Filter pengiriman: cocok pada NOMOR PENGIRIMAN (kode `#XXXXXXXX`, dengan
 * atau tanpa `#`), RESI, NAMA PENERIMA, ALAMAT (line + kota), EKSPEDISI,
 * serta tiap item (nama produk / isi variasi / catatan variasi).
 */
export function filterShipments<
  S extends {
    id?: string;
    recipientName?: string | null;
    shipAddressLine?: string | null;
    shipCity?: string | null;
    resiNumber?: string | null;
    expedition?: string | null;
    items: Array<{
      productName: string;
      variantName: string;
      note?: string | null;
    }>;
  }
>(shipments: S[], query: string): S[] {
  const q = normalizeSearch(query);
  if (!q) return shipments;
  return shipments.filter((s) => {
    if (s.id) {
      const code = shipmentCode(s.id).toLowerCase(); // '#xxxxxxxx'
      if (code.includes(q) || code.slice(1).includes(q)) return true;
    }
    if (
      normalizeSearch(s.resiNumber).includes(q) ||
      normalizeSearch(s.recipientName).includes(q) ||
      normalizeSearch(s.shipAddressLine).includes(q) ||
      normalizeSearch(s.shipCity).includes(q) ||
      normalizeSearch(s.expedition).includes(q)
    ) {
      return true;
    }
    return s.items.some(
      (item) =>
        item.productName.toLowerCase().includes(q) ||
        item.variantName.toLowerCase().includes(q) ||
        normalizeSearch(item.note).includes(q)
    );
  });
}

/**
 * Label fase pengiriman (shipment_requests.status) untuk tampilan — netral agar
 * dipakai sisi admin maupun pembeli (mis. kartu "Pengiriman yang Ditagih" pada
 * nota ongkir). Status tak dikenal dikembalikan apa adanya.
 */
export function shipmentStageLabel(status: string | null | undefined): string {
  switch (status) {
    case "draft":
      return "Disiapkan";
    case "waiting_payment":
      return "Menunggu Bayar Ongkir";
    case "paid":
      return "Siap Dikirim";
    case "shipped":
      // Sama dengan tab pembeli Dikemas/Dikirim/Selesai (uji nyata S01 T4).
      return "Dikirim";
    case "done":
      return "Diterima";
    default:
      return status ?? "—";
  }
}

/**
 * Label paket `paid` sisi admin — SATU aturan untuk Detail Pengiriman, kartu
 * pengiriman di tagihan, dan daftar Pengiriman (uji nyata S01 T3: paket yang
 * sudah ditandai Siap Kirim dulu tetap "Siap Packing" di detailnya karena
 * `ready_to_ship_at` tak dibaca). Status data tetap 'paid'; Siap Kirim =
 * penanda kolom (mig 4430).
 */
export function adminPaidShipmentLabel(
  readyToShipAt: string | null | undefined,
  piutang: boolean
): string {
  const base = readyToShipAt ? "Siap Kirim" : "Siap Packing";
  return piutang ? `${base} · Piutang` : base;
}

/**
 * Label fase pengiriman sisi admin (kartu "Pengiriman yang ditagih" di tagihan
 * ongkir) — kosakata yang sama dengan chip Detail Pengiriman & daftar.
 */
export function adminShipmentStageLabel(
  status: string | null | undefined,
  readyToShipAt?: string | null
): string {
  switch (status) {
    case "draft":
      return "Atur Ongkir";
    case "waiting_payment":
      return "Menunggu Bayar Ongkir";
    case "paid":
      return adminPaidShipmentLabel(readyToShipAt, false);
    case "shipped":
      return "Sedang Dikirim";
    case "done":
      return "Diterima ✓";
    case "cancelled":
      return "Dibatalkan";
    default:
      return status ?? "—";
  }
}

/**
 * Label NOMINAL ongkir sebuah pengiriman — SATU aturan untuk kartu board
 * admin, Detail Pengiriman admin, dan Detail Pengiriman pembeli.
 *
 * `Rp 0` di `shipment_requests.ongkir` punya DUA makna yang dulu dirender
 * sama ("—") sehingga gratis terbaca "belum ditentukan" (bug zzhomey
 * 2026-08-19, KRM-2608190014/-0015): pada shipment `draft` ongkir memang
 * BELUM ditentukan (Set Ongkir belum dijalankan), sedangkan pada fase
 * sesudahnya 0 adalah keputusan sadar GRATIS (Set Ongkir "Gratis/Tanpa
 * Tagihan", ACC combo Rp 0, Ambil di Tempat). Pembedanya STATUS, bukan
 * nominal: `waiting_payment` ke atas hanya tercapai lewat mesin yang
 * MENETAPKAN ongkir (`set_shipment_ongkir` / `try_fulfill_shipment_intent`
 * ber-`ongkir_amount` terisi / `admin_release_unpaid_shipment`), jadi 0 di
 * fase itu selalu gratis — bukan lubang data.
 */
export function shipmentOngkirLabel(
  ongkir: number,
  status: string | null | undefined,
  /**
   * `shipment_requests.ongkir_paid_by_buyer` (mig 4040) — pembeli membayar
   * kurirnya SENDIRI (resi sendiri), jadi tak ada yang ditagih.
   *
   * Menang atas kedua aturan di bawah, termasuk atas status `draft`: penanda
   * ini hanya bisa muncul lewat keputusan sadar ("sudah dibayar pembeli"),
   * jadi tak ada keadaan di mana ia benar TAPI ongkirnya belum ditentukan.
   * Dan ia HARUS dibedakan dari "Gratis": keduanya Rp 0 di kolom uang, tapi
   * hanya "Gratis" yang berarti TOKO menanggung biayanya.
   */
  paidByBuyer?: boolean | null
): string {
  if (paidByBuyer) return "Dibayarkan pembeli";
  if (ongkir > 0) return `Rp ${Math.round(ongkir).toLocaleString("id-ID")}`;
  return status === "draft" ? "Belum ditentukan" : "Gratis";
}

// ─── Status chip pengiriman (storefront) ─────────────────────────────────────

export type ShipmentStatusMeta = { label: string; color: string };

const SHIPMENT_STATUS_META: Record<string, ShipmentStatusMeta> = {
  draft: { label: "Menunggu ongkir", color: "bg-zinc-100 text-zinc-600" },
  waiting_payment: {
    label: "Menunggu pembayaran ongkir",
    color: "bg-marigold/15 text-marigold"
  },
  paid: { label: "Siap dikirim", color: "bg-leaf/15 text-leaf" },
  shipped: { label: "Dikirim", color: "bg-indigo-100 text-indigo-700" },
  done: { label: "Selesai", color: "bg-leaf/15 text-leaf" }
};

/**
 * Chip status kartu/halaman pengiriman pembeli. Kasus "Kirim Tanpa Lunas"
 * (mig 073: shipment maju ke 'paid' tapi nota ongkir masih terbuka) diberi
 * label khusus supaya tidak tampil "Siap dikirim" polos padahal masih ada
 * tagihan. Dipakai `ShipmentCard` (OrdersClient) & `ShipmentDetailClient`.
 */
export function shipmentStatusMeta(
  s: {
    status: string;
    paymentStatus: string;
    ongkirInvoiceStatus?: string | null;
    /** Penanda Siap Kirim (mig 4430) — dibaca label admin saja. */
    readyToShipAt?: string | null;
  },
  /**
   * `admin` (kartu Status Pengiriman di detail tagihan admin): paket `paid`
   * memakai kosakata Detail Pengiriman admin — "Siap Packing", dan
   * "Siap Packing · Piutang" selama pembayarannya masih `pending` (tagihan
   * ongkir terpisah ATAU ongkir menyatu di tagihan yang belum lunas, mig 8910).
   */
  opts: { audience?: "admin" } = {}
): ShipmentStatusMeta {
  if (opts.audience === "admin" && s.status === "paid") {
    const piutang = s.paymentStatus === "pending";
    return {
      label: adminPaidShipmentLabel(s.readyToShipAt, piutang),
      color: piutang ? "bg-amber-100 text-amber-700" : "bg-leaf/15 text-leaf"
    };
  }
  // Fase lain sisi admin: nama yang sama dengan Detail Pengiriman & daftar
  // (mis. "Sedang Dikirim", bukan "Dikirim"/"Terkirim" — uji nyata S01 T4).
  if (opts.audience === "admin" && SHIPMENT_STATUS_META[s.status]) {
    return {
      label: adminShipmentStageLabel(s.status),
      color: SHIPMENT_STATUS_META[s.status].color
    };
  }
  const ongkirUnpaid =
    s.ongkirInvoiceStatus === "open" || s.ongkirInvoiceStatus === "partial";
  if (s.status === "paid" && s.paymentStatus !== "verified" && ongkirUnpaid) {
    return {
      label: "Disiapkan · tagihan terpisah",
      color: "bg-amber-100 text-amber-700"
    };
  }
  return (
    SHIPMENT_STATUS_META[s.status] ?? {
      label: s.status,
      color: "bg-zinc-100 text-zinc-600"
    }
  );
}

// ─── Nota DP per pesanan: aktif vs hangus (mig 6590) ─────────────────────────

/**
 * Tagihan barang yang KINI menagih nominal DP pesanan (nota DP-nya hangus,
 * mig 6590), siap tampil. Sejak bughunt G7-12 hanya terisi bila penyebabnya
 * masih menagih pesanan itu (`judgeHangusCause`) — dan menunjuk UJUNG rantai
 * gabungnya (tagihan gabungan yang hidup), bukan sumber yang sudah diarsipkan.
 */
export type DpHangusInfo = {
  /** Nota DP yang hangus. */
  dpInvoiceId: string;
  goodsInvoiceId: string;
  /** "INV2609280014" / "#12". */
  goodsDisplayNo: string;
  /**
   * Status tagihan barang itu — penentu cara bayar (`dpBilledCartTail`):
   * belum ada uang → Bayar DP, sudah ada uang → Pelunasan, lunas → selesai
   * (bughunt 2026-10-13). null/absen = tak termuat → kalimat Bayar DP lama.
   */
  goodsStatus?: string | null;
};

/**
 * Nota DP hangus yang penyebabnya TAK LAGI menagih pesanan itu ("yatim",
 * istilah mig 7100): tagihan barangnya dibatalkan, atau baris pesanan ini
 * sudah dibatalkan darinya. DP-nya tidak ditagih di mana pun — admin boleh
 * menerbitkannya lagi (server membuka kembali nota lamanya, mig 7030).
 */
export type DpHangusOrphanInfo = {
  /** Nota DP yang hangus. */
  dpInvoiceId: string;
  /** Tagihan barang yang dulu menagihnya (ujung rantai gabung penyebab). */
  goodsInvoiceId: string;
  goodsDisplayNo: string;
  reason: HangusOrphanReason;
};

/** Embed `hangus_by` (dan `merged_into` bersarang di dalamnya). */
type DpCauseEmbed = {
  invoice_no?: number | null;
  invoice_code?: string | null;
  status?: string | null;
  merged_into_invoice_id?: string | null;
  merged_into?: DpCauseEmbed | DpCauseEmbed[] | null;
};

/** Baris mentah `invoices` (kind='dp') dari query keranjang. */
export type DpInvoiceRowLike = {
  id: string;
  status: string;
  source_order_id: string | null;
  hangus_by_invoice_id?: string | null;
  /** mig 7130 — nota DP kedaluwarsa karena barang sudah ready semua. */
  dp_expired_at?: string | null;
  hangus_by?: DpCauseEmbed | DpCauseEmbed[] | null;
};

/** Tagihan barang HIDUP yang memuat baris produk aktif sebuah pesanan. */
export type CartBillingInvoice = {
  id: string;
  invoiceNo: number | null;
  invoiceCode: string | null;
  status: string;
};

const ACTIVE_DP_STATUSES = new Set(["pending_acc", "open", "partial", "paid"]);

/** Tagihan barang yang masih menagih (cermin `hangus_dp_cause_bills_order`). */
const BILLING_GOODS_STATUSES = new Set(["open", "partial", "paid"]);

/**
 * Tagihan barang HIDUP (open/partial/paid) yang masih memuat baris
 * `product_item` AKTIF tiap pesanan — dari baris `invoice_lines` keranjang
 * (query `lineRows` yang sama: sudah tersaring `cancelled_at is null`, status
 * nota terlihat, plus embed `invoices(id, invoice_no, invoice_code, status,
 * kind)`). Bahan "penyebab DP hangus masih menagih?" (bughunt G7-12/G7-13)
 * tanpa round-trip baru. Baris tanpa `kind` (warisan) dianggap baris produk,
 * seperti `buildCartInvoiceIndex`.
 */
export function cartBillingInvoicesByOrder(
  lineRows: readonly any[],
  orderIdByItem: ReadonlyMap<string, string>
): Map<string, Map<string, CartBillingInvoice>> {
  const out = new Map<string, Map<string, CartBillingInvoice>>();
  for (const row of lineRows ?? []) {
    if (row?.cancelled_at) continue;
    if (row?.kind != null && row.kind !== "product_item") continue;
    const orderId = row?.order_item_id ? orderIdByItem.get(row.order_item_id) : undefined;
    if (!orderId) continue;
    const inv = Array.isArray(row.invoices) ? row.invoices[0] : row.invoices;
    if (!inv?.id || !BILLING_GOODS_STATUSES.has(inv.status)) continue;
    if (inv.kind != null && inv.kind !== "product") continue;
    let byId = out.get(orderId);
    if (!byId) {
      byId = new Map();
      out.set(orderId, byId);
    }
    if (!byId.has(inv.id)) {
      byId.set(inv.id, {
        id: inv.id,
        invoiceNo: inv.invoice_no ?? null,
        invoiceCode: inv.invoice_code ?? null,
        status: inv.status
      });
    }
  }
  return out;
}

type CauseNode = HangusCauseNode & { displayNo: string };

const NO_BILLING: ReadonlyMap<string, CartBillingInvoice> = new Map();

function firstEmbed(e: DpCauseEmbed | DpCauseEmbed[] | null | undefined): DpCauseEmbed | null {
  return (Array.isArray(e) ? e[0] : e) ?? null;
}

/**
 * Tagihan barang hidup TERTUA yang menagih pesanan (nomor terkecil — cermin
 * urutan `coalesce(approved_at, created_at), invoice_no` server, yang tak
 * termuat di sini; nomor nota naik seiring terbitnya).
 */
function earliestBilling(
  billing: ReadonlyMap<string, CartBillingInvoice> | undefined
): CartBillingInvoice | null {
  let best: CartBillingInvoice | null = null;
  for (const b of billing?.values() ?? []) {
    if (
      !best ||
      (b.invoiceNo ?? Number.MAX_SAFE_INTEGER) < (best.invoiceNo ?? Number.MAX_SAFE_INTEGER)
    ) {
      best = b;
    }
  }
  return best;
}

/**
 * Putusan penyebab satu nota DP hangus dari embed bersarangnya + tagihan
 * barang hidup pesanan itu. Tagihan hidup didaftarkan LEBIH DULU (statusnya
 * pasti, tak digabung); embed hanya melengkapi simpul yang belum dikenal.
 */
function judgeCartDpCause(
  r: DpInvoiceRowLike,
  billing: ReadonlyMap<string, CartBillingInvoice> | undefined,
  formatNo: (no: number | null, code: string | null) => string
): HangusCauseVerdict<CauseNode> {
  const nodes = new Map<string, CauseNode>();
  for (const b of billing?.values() ?? []) {
    nodes.set(b.id, {
      id: b.id,
      status: b.status,
      mergedIntoId: null,
      displayNo: formatNo(b.invoiceNo, b.invoiceCode)
    });
  }
  let id: string | null = r.hangus_by_invoice_id ?? null;
  let emb = firstEmbed(r.hangus_by);
  for (let hop = 0; id && emb && hop <= HANGUS_CHAIN_MAX_HOPS; hop += 1) {
    if (!nodes.has(id)) {
      nodes.set(id, {
        id,
        status: typeof emb.status === "string" ? emb.status : null,
        mergedIntoId: emb.merged_into_invoice_id ?? null,
        displayNo: formatNo(emb.invoice_no ?? null, emb.invoice_code ?? null)
      });
    }
    id = emb.merged_into_invoice_id ?? null;
    emb = firstEmbed(emb.merged_into);
  }
  return judgeHangusCause({
    causeId: r.hangus_by_invoice_id ?? "",
    nodes,
    activeLineInvoiceIds: billing ? new Set(billing.keys()) : undefined
  });
}

/**
 * Pisahkan nota DP per pesanan jadi AKTIF (yang menentukan "DP Lunas / Belum
 * Lunas"), HANGUS (void ber-`hangus_by_invoice_id` yang penyebabnya masih
 * menagih — DP-nya ikut ditagih di tagihan barang itu), YATIM (hangus,
 * tapi penyebabnya tak lagi menagih pesanan itu), dan KEDALUWARSA (void
 * ber-`dp_expired_at`, mig 7130 — barang sudah ready semua sebelum DP
 * dibayar; DP tak berlaku, butuh `dp_expired_at` di select). Nota aktif selalu menang:
 * pesanan yang DP-nya pernah hangus lalu diterbitkan ulang kembali ditagih DP
 * biasa. Void TANPA penyebab (pesanan batal, PO dibuka ulang, manual)
 * diabaikan — seperti sebelumnya.
 *
 * Sebelum ini keranjang admin & /orders hanya membaca nota aktif, sehingga
 * pesanan ber-DP hangus terbaca "belum ada nota DP": pembeli disuruh
 * "Bayar DP di Tagihan" untuk DP yang tak ada, admin melihat tombol
 * "Terbitkan Nota DP" yang tak menghasilkan apa-apa.
 *
 * Bughunt G7-12: SETIAP DP hangus dulu dianggap "ditagih di tagihan barang
 * X" walau X sudah dibatalkan — tombol Terbitkan Nota DP ikut disembunyikan
 * dan jalur pemulihan mig 7030 tak terjangkau. Kini penyebabnya dinilai
 * (`judgeHangusCause`): butuh `status` + `merged_into_invoice_id` di embed
 * `hangus_by` (bersarang untuk rantai gabung), dan `billingByOrder` (tagihan
 * barang hidup ber-baris produk aktif per pesanan, `cartBillingInvoicesByOrder`)
 * bila barisnya termuat. Rantai yang tak termuat utuh → tampilan lama
 * (dianggap masih menagih) — ragu = jangan ubah apa yang dilihat orang.
 */
export function pickDpInvoicesByOrder<R extends DpInvoiceRowLike>(
  rows: ReadonlyArray<R>,
  formatNo: (no: number | null, code: string | null) => string,
  billingByOrder?: ReadonlyMap<string, ReadonlyMap<string, CartBillingInvoice>>
): {
  active: Map<string, R>;
  hangus: Map<string, DpHangusInfo>;
  orphaned: Map<string, DpHangusOrphanInfo>;
  /** Pesanan yang nota DP-nya kedaluwarsa (tanpa nota DP aktif pengganti). */
  expired: Set<string>;
} {
  const active = new Map<string, R>();
  const hangus = new Map<string, DpHangusInfo>();
  const orphaned = new Map<string, DpHangusOrphanInfo>();
  const expired = new Set<string>();
  for (const r of rows) {
    const orderId = r.source_order_id;
    if (!orderId) continue;
    if (ACTIVE_DP_STATUSES.has(r.status)) {
      active.set(orderId, r);
      continue;
    }
    // mig 7130: penanda server — tak pernah bersamaan dengan penyebab hangus
    // (kedaluwarsa melepas penyebabnya; dibuka ke tagihan barang mengosongkan
    // penandanya).
    if (r.status === "void" && r.dp_expired_at) {
      expired.add(orderId);
      continue;
    }
    if (r.status !== "void" || !r.hangus_by_invoice_id) continue;
    if (hangus.has(orderId) || orphaned.has(orderId)) continue;
    // Baris TERMUAT tapi pesanan ini tak ada di tagihan hidup mana pun = himpunan
    // KOSONG (bukan "tak termuat") — kalau tidak, penyebab hidup yang barisnya
    // sudah dibatalkan (G7-13) lolos sebagai "masih menagih".
    const billing = billingByOrder ? billingByOrder.get(orderId) ?? NO_BILLING : undefined;
    const verdict = judgeCartDpCause(r, billing, formatNo);
    // Penyebab tak lagi menagih, TAPI pesanan ini ditagih di tagihan barang
    // hidup lain → server memindahkan penyebabnya ke sana (pemilih tagihan
    // tertua: `reopen_hangus_dp_internal` 7030/7100, sekali jalan 7100 §8,
    // trigger pembatalan baris 7100). DP-nya memang ikut ditagih di tagihan
    // itu — bukan "tidak ditagih di mana pun", dan tak perlu tombol Terbitkan.
    const elsewhere = verdict.kind === "orphan" ? earliestBilling(billing) : null;
    if (elsewhere) {
      hangus.set(orderId, {
        dpInvoiceId: r.id,
        goodsInvoiceId: elsewhere.id,
        goodsDisplayNo: formatNo(elsewhere.invoiceNo, elsewhere.invoiceCode),
        goodsStatus: elsewhere.status
      });
    } else if (verdict.kind === "orphan") {
      orphaned.set(orderId, {
        dpInvoiceId: r.id,
        goodsInvoiceId: verdict.end.id,
        goodsDisplayNo: verdict.end.displayNo,
        reason: verdict.reason
      });
    } else if (verdict.kind === "bills") {
      hangus.set(orderId, {
        dpInvoiceId: r.id,
        goodsInvoiceId: verdict.end.id,
        goodsDisplayNo: verdict.end.displayNo,
        goodsStatus: verdict.end.status
      });
    } else {
      const emb = firstEmbed(r.hangus_by);
      hangus.set(orderId, {
        dpInvoiceId: r.id,
        goodsInvoiceId: r.hangus_by_invoice_id,
        goodsDisplayNo: formatNo(emb?.invoice_no ?? null, emb?.invoice_code ?? null),
        // Rantai tak termuat utuh → status penyebab tercatat (bisa "void"
        // karena digabung → kalimat Bayar DP lama, arah aman).
        goodsStatus: typeof emb?.status === "string" ? emb.status : null
      });
    }
  }
  for (const orderId of active.keys()) {
    hangus.delete(orderId);
    orphaned.delete(orderId);
    expired.delete(orderId);
  }
  // Pesanan yang punya DP hangus (lebih baru) tak lagi "kedaluwarsa" di mata
  // keranjang: nominalnya ikut ditagih di tagihan barang.
  for (const orderId of hangus.keys()) expired.delete(orderId);
  return { active, hangus, orphaned, expired };
}

/**
 * Pesanan yang DP-nya ikut ditagih di tagihan barang yang MASIH menagihnya —
 * badge "Ditagih di Tagihan Barang" papan keranjang semua pembeli. Papan tak
 * memuat baris nota, jadi yang dinilai hanya status rantai penyebabnya:
 * penyebab yang sudah dibatalkan → badge kembali "DP Belum Lunas" (G7-12);
 * baris yang dibatalkan dari tagihan yang tetap hidup (G7-13) baru terlihat di
 * keranjang per pembeli.
 */
export function billedDpHangusOrderIds(rows: ReadonlyArray<DpInvoiceRowLike>): Set<string> {
  return new Set(pickDpInvoicesByOrder(rows, () => "").hangus.keys());
}
